import {
  axiosDeleteRequest,
  axiosGetPaginatedRequest,
  axiosGetRequest,
  axiosPatchRequest,
  axiosPostPaginatedRequest,
  axiosPostRequest,
  axiosPutRequest,
  getCommunityRoute,
  getCommunityRouteV3,
  getIdentityRoute,
} from '../../api';
import { CreateLikeDto } from '../../common';
import { CreatePostDto, ExternalUrlDto, UpdatePostDto } from './dtos';
import { ModeratorEditPostDto, OpenGraphMetaTags, Post } from './models';

const PAGE_SIZE = 7;

const getRoomsRouteV3 = () => `${getCommunityRouteV3()}/room`;
const getPostsRouteV3 = () => `${getCommunityRouteV3()}/post`;
const getPostRouteV3 = () => `${getCommunityRouteV3()}/room/post/{postId}`;
const getReportPostRoute = () => `${getCommunityRoute()}/post/{postId}/report`;
const getPostsFeedRoute = () => `${getCommunityRouteV3()}/posts`;
const getLikePostRoute = () =>
  `${getCommunityRoute()}/room/{roomId}/post/{postId}/like`;
const getModeratorPostRoute = () => `${getCommunityRoute()}/moderator/post`;

export const fetchRoomPostsCall = (
  roomId: string,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<Post[]>(
    `${getRoomsRouteV3()}/${roomId}/posts/${numrecs || PAGE_SIZE}`,
    xMsContinuationToken
  );
};

export const createPostCall = (newPost: CreatePostDto, roomId: string) => {
  return axiosPostRequest<CreatePostDto, Post>(
    `${getRoomsRouteV3()}/${roomId}/post/`,
    newPost
  );
};

export const createPostInMultipleRoomsCall = (newPost: CreatePostDto) => {
  return axiosPostRequest<CreatePostDto, Post>(
    `${getCommunityRoute()}/post/`,
    newPost
  );
};

export const updatePostCall = (updatedPost: UpdatePostDto, postId: string) => {
  return axiosPutRequest<UpdatePostDto, Post>(
    `${getPostsRouteV3()}/${postId}`,
    updatedPost
  );
};

export const deletePostCall = (postId: string) => {
  return axiosDeleteRequest<string, void>(`${getPostsRouteV3()}/${postId}`);
};

export const fetchFeedPostsCall = (
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<Post[]>(
    `${getPostsFeedRoute()}/${
      numrecs || PAGE_SIZE
    }?checkifmemberjoinedroom=true`,
    xMsContinuationToken
  );
};

export const updatePostLikeCall = (
  like: CreateLikeDto,
  roomId: string,
  postId: string
) => {
  return axiosPostRequest<CreateLikeDto, Post>(
    getLikePostRoute().replace('{roomId}', roomId).replace('{postId}', postId),
    like
  );
};

export const fetchPostCall = (postId: string) => {
  return axiosGetRequest<Post>(getPostRouteV3().replace('{postId}', postId));
};

export const reportPostCall = (postId: string) => {
  return axiosPatchRequest(getReportPostRoute().replace('{postId}', postId));
};

export const moderatorDeletePostCall = (postId: string) => {
  return axiosDeleteRequest(`${getCommunityRoute()}/moderator/post/${postId}`);
};

export const moderatorEditPostCall = (
  postId: string,
  editPostDto: ModeratorEditPostDto
) => {
  return axiosPutRequest<ModeratorEditPostDto, Post>(
    `${getCommunityRoute()}/moderator/post/${postId}`,
    editPostDto
  );
};

export const pinPostToRoomCall = (storyId: string) => {
  return axiosPatchRequest(
    `${getCommunityRoute()}/moderator/post/${storyId}/pin`
  );
};

export const unpinPostFromRoomCall = (storyId: string) => {
  return axiosPatchRequest(
    `${getCommunityRoute()}/moderator/post/${storyId}/unpin`
  );
};

export const fetchOpenGraphMetaTagsCall = (url: ExternalUrlDto) => {
  return axiosPostRequest<ExternalUrlDto, OpenGraphMetaTags>(
    `${getCommunityRoute()}/metadata`,
    url
  );
};

export const fetchMyRoomsFeedPostsCall = (
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosGetPaginatedRequest<Post[]>(
    `${getIdentityRoute()}/member/rooms/posts/${numrecs || PAGE_SIZE}`,
    xMsContinuationToken
  );
};

export const fetchFeedPostsByTopicsCall = (
  topics: { topics: string[] },
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosPostPaginatedRequest<{ topics: string[] }, Post[]>(
    `${getCommunityRouteV3()}/topic/posts/${numrecs || PAGE_SIZE}`,
    topics,
    xMsContinuationToken
  );
};

export const movePostToAnotherRoomCall = (postId: string, roomId: string) => {
  return axiosPutRequest<undefined, string>(
    `${getModeratorPostRoute()}/${postId}/room/${roomId}`
  );
};
